import { useEffect, useId, useRef, useState } from 'react';

import { listarJogadores } from '../../jogadores/api/jogadores';
import type { Jogador } from '../../jogadores/api/jogadores';
import { Aviso } from '../../participantes/ui/Aviso';
import { ConfirmacaoDeSituacao } from '../../participantes/ui/ConfirmacaoDeSituacao';
import { MenuSuspenso } from '../../participantes/ui/MenuDeAcoes';
import { estacaoAtualDoCard, formatarTempoDoLider, obterFaseAtual, obterResumoDoGrupo, rotuloDoGrupo as rotuloDe } from '../api/operacao';
import type { BonusDoLaboratorio, CardDoGrupo, FaseAtualDoGrupo, FaseDoResumo } from '../api/operacao';
import { SITUACAO, itensDaLinha, outroAtivo } from './acoesDaLinha';
import { AlertaOffline } from './AlertaOffline';
import { ConfirmacaoDeAlocacao } from './ConfirmacaoDeAlocacao';
import { ConfirmacaoDeDesfazer } from './ConfirmacaoDeDesfazer';
import { ConfirmacaoDeEstacao } from './ConfirmacaoDeEstacao';
import { ConfirmacaoDeExcluirGrupo, ConfirmacaoDeExcluirParticipante } from './ConfirmacaoDeExclusao';
import { ConfirmacaoDeInicio } from './ConfirmacaoDeInicio';
import { ConfirmacaoDeLideranca } from './ConfirmacaoDeLideranca';
import { prenderFoco } from './foco';
import { PontuacaoDoJogador } from './PontuacaoDoJogador';
import { useAcoesDoGrupo } from './useAcoesDoGrupo';
import { SeloDeConexao } from '../../participantes/ui/SeloDeConexao';

interface ModalDoGrupoProps {
    turmaId: string;
    csrfToken?: string;
    /** O card da Operação: o cabeçalho do modal repete o que ele já sabe (`DP-RA-031`). */
    grupo: CardDoGrupo;
    aoFechar: () => void;
    /** Reiniciar/Finalizar valeu: a Operação relê o card (`RA6`); o resumo o modal relê sozinho. */
    aoMudarEstacao: () => void;
    /** Um participante saiu do grupo (exclusão): a Operação relê o card. */
    aoMudarMembros?: () => void;
    /** O grupo foi excluído (`RA8`): a Operação fecha o modal, avisa e relê. */
    aoExcluirGrupo?: (rotulo: string) => void;
}

const BONUS_DO_LABORATORIO: Record<BonusDoLaboratorio, string> = {
    concluido: 'Bônus concluído',
    liberado: 'Bônus liberado',
    nao_liberado: 'Bônus não foi liberado',
    aguardando: 'Bônus ainda não liberado',
};

/** Rótulo da caixa: no Laboratório o modelo fala do bônus, não dos pontos. */
function rotuloDoResumo(fase: FaseDoResumo) {
    return fase.fase === 'labpixels' ? 'Lab. de Pixels (bônus)' : fase.rotulo;
}

/** Só formata o que a rota trouxe: a separação base + bônus é do servidor (`RN-RA-009`). */
function textoDoResumo(fase: FaseDoResumo) {
    if (fase.fase === 'labpixels') return BONUS_DO_LABORATORIO[fase.bonus_lab ?? 'aguardando'];
    if (fase.base !== null && fase.bonus !== null && fase.colocacao !== null) {
        return `${fase.base} + ${fase.bonus} (Bônus de ${fase.colocacao}º lugar)`;
    }
    if (fase.pontos !== null) return `${fase.pontos} pts`;
    return fase.estado === 'em_andamento' ? 'Em andamento' : '–';
}

const VERBO_DO_COMANDO = { iniciar: 'Iniciar', reiniciar: 'Reiniciar' } as const;

/** Com o modal aberto, card, resumo e comandos são relidos neste intervalo: a fase fecha no banco. */
const INTERVALO_DO_MODAL_MS = 5_000;

/** O card com a fase que o banco informou por último: é ela que manda no cabeçalho e nos comandos. */
function comFaseDoBanco(grupo: CardDoGrupo, banco: FaseAtualDoGrupo | null): CardDoGrupo {
    if (!banco) return grupo;
    const fase_atual = banco.aberta && banco.fase
        ? { fase: banco.fase, rotulo: banco.rotulo ?? banco.fase, minutos: banco.minutos, pausada: banco.pausada }
        : null;
    return { ...grupo, fase_atual };
}

/** Só uma resposta da rota vale: outra coisa (falha, corpo inesperado) deixa a fase do card. */
function ehFaseDoBanco(resposta: unknown): resposta is FaseAtualDoGrupo {
    return typeof (resposta as FaseAtualDoGrupo | null)?.aberta === 'boolean';
}

function faseAtual(grupo: CardDoGrupo) {
    if (grupo.finalizado) return 'Finalizado';
    return grupo.fase_atual?.rotulo ?? null;
}

/**
 * Modal do grupo (`RA3`, modelo `docs/img_refac/img_1.png`): cabeçalho, resumo por fase e a tabela
 * dos participantes com pesquisa. Os comandos do modelo entram com os fluxos deles (`DP-RA-028`).
 */
export function ModalDoGrupo({
    turmaId, csrfToken = '', grupo, aoFechar, aoMudarEstacao, aoMudarMembros = () => undefined, aoExcluirGrupo = () => undefined,
}: ModalDoGrupoProps) {
    const idDoTitulo = useId();
    const janela = useRef<HTMLDivElement>(null);
    const [resumo, setResumo] = useState<FaseDoResumo[] | null>(null);
    const [erroResumo, setErroResumo] = useState(false);
    const [membros, setMembros] = useState<Jogador[] | null>(null);
    const [erroMembros, setErroMembros] = useState(false);
    // A turma inteira: é onde o "Alocar" procura a matrícula (`RN-RA-020`).
    const [todos, setTodos] = useState<Jogador[]>([]);
    const [pesquisa, setPesquisa] = useState('');
    const [matriculaParaAlocar, setMatriculaParaAlocar] = useState('');
    const [faseDoBanco, setFaseDoBanco] = useState<FaseAtualDoGrupo | null>(null);
    const rotuloDoGrupo = rotuloDe(grupo);
    const cardAtual = comFaseDoBanco(grupo, faseDoBanco);
    const estacaoAtual = estacaoAtualDoCard(cardAtual);
    const {
        menuAberto, alternarMenu, fecharMenu, confirmacao, fecharConfirmacao, processando,
        aviso, fecharAviso, escolher, enviar, buscarParaAlocar, escolherEstacao, enviarEstacao,
        excluir, excluirOGrupo, escolherExcluirGrupo, escolherIniciar, enviarIniciar, porCima, popupAberto,
    } = useAcoesDoGrupo({
        turmaId, grupoId: grupo.grupo_id, grupoRotulo: rotuloDoGrupo, csrfToken, membros, setMembros, todos, setTodos,
        estacaoAtual, aoMudarMembros, aoExcluirGrupo,
        aoMudarEstacao: () => reler(),
    });

    /** Relê o card (pela Operação), o resumo e os comandos. Falha na releitura deixa o que estava na tela. */
    function reler() {
        aoMudarEstacao();
        obterResumoDoGrupo(turmaId, grupo.grupo_id).then((resposta) => setResumo(resposta.fases)).catch(() => undefined);
        // Depois de um comando do próprio modal o cabeçalho não espera o próximo ciclo de 5 s.
        obterFaseAtual(turmaId, grupo.grupo_id)
            .then((resposta) => {
                if (!ehFaseDoBanco(resposta)) return;
                ultimaFase.current = resposta.aberta ? resposta.fase : null;
                setFaseDoBanco(resposta);
            })
            .catch(() => undefined);
    }
    const relerAgora = useRef(reler);
    relerAgora.current = reler;

    // A fase fecha sem o painel saber (o jogador terminou e a estação finalizou): o modal pergunta ao
    // banco pela `fase-atual`. Quando ela muda, relê o resumo (os botões das caixas) e o card.
    const ultimaFase = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        let ativo = true;
        async function perguntar() {
            try {
                const resposta = await obterFaseAtual(turmaId, grupo.grupo_id);
                if (!ativo || !ehFaseDoBanco(resposta)) return;
                const chave = resposta.aberta ? resposta.fase : null;
                const mudou = ultimaFase.current !== undefined && ultimaFase.current !== chave;
                ultimaFase.current = chave;
                setFaseDoBanco(resposta);
                if (mudou) relerAgora.current();
            } catch {
                // Falha na leitura: fica o que já estava na tela.
            }
        }
        void perguntar();
        const relogio = window.setInterval(() => {
            if (document.visibilityState === 'visible') void perguntar();
        }, INTERVALO_DO_MODAL_MS);
        return () => {
            ativo = false;
            window.clearInterval(relogio);
        };
    }, [turmaId, grupo.grupo_id]);

    useEffect(() => {
        janela.current?.focus();
        function aoTeclar(evento: KeyboardEvent) {
            if (porCima.current && evento.key === 'Escape') return;
            if (evento.key === 'Escape') aoFechar();
            if (evento.key === 'Tab' && janela.current && !popupAberto.current) prenderFoco(janela.current, evento);
        }
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aoFechar]);

    useEffect(() => {
        let ativo = true;
        obterResumoDoGrupo(turmaId, grupo.grupo_id)
            .then((resposta) => ativo && setResumo(resposta.fases))
            .catch(() => ativo && setErroResumo(true));
        listarJogadores(turmaId)
            .then((resposta) => {
                if (!ativo) return;
                setTodos(resposta.jogadores);
                setMembros(resposta.jogadores.filter((j) => j.grupo_id === grupo.grupo_id));
            })
            .catch(() => ativo && setErroMembros(true));
        return () => {
            ativo = false;
        };
    }, [turmaId, grupo.grupo_id]);

    const termo = pesquisa.trim().toLowerCase();
    const visiveis = (membros ?? []).filter(
        (membro) => !termo || membro.login.toLowerCase().includes(termo) || (membro.nome ?? '').toLowerCase().includes(termo),
    );
    const atual = faseAtual(cardAtual);

    /**
     * Só quando o inativado é o líder e a tabela mostra outro membro ativo e não bloqueado que fique
     * (`DP-RA-041`, `RN-RA-041`); o sorteio em si é do servidor (`RN-RA-038`).
     */
    function fraseDoSorteio(jogador: Jogador, origem: string) {
        if (!jogador.lider || !outroAtivo(jogador, todos)) return undefined;
        return `Como é líder do ${origem}, o sistema sorteará outro(a) líder entre os membros ativos que ficarem.`;
    }

    return (
        <div className="fundo-modal-grupo">
            <div className="modal-grupo" role="dialog" aria-modal="true" aria-labelledby={idDoTitulo} tabIndex={-1} ref={janela}>
                <button type="button" className="fechar-modal-grupo" aria-label="Fechar" onClick={aoFechar}>
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M6 6l12 12M18 6 6 18" />
                    </svg>
                </button>
                <h2 id={idDoTitulo}>{rotuloDoGrupo}</h2>
                <div className="cabecalho-modal-grupo">
                    <p><strong>{`${grupo.online}/${grupo.participantes}`}</strong> participantes online</p>
                    {grupo.online < grupo.participantes && <AlertaOffline />}
                    {atual && (
                        <>
                            <span className="separador-modal" aria-hidden="true">·</span>
                            <p className="fase-atual-modal">{`Fase atual: ${atual}`}</p>
                        </>
                    )}
                    {estacaoAtual && (
                        <div className="comandos-da-estacao">
                            <button type="button" className="botao-comando-estacao" onClick={() => escolherEstacao('reiniciar')} disabled={processando}>
                                Reiniciar
                            </button>
                            <button type="button" className="botao-comando-estacao botao-finalizar-estacao" onClick={() => escolherEstacao('finalizar')} disabled={processando}>
                                Finalizar
                            </button>
                        </div>
                    )}
                </div>

                {erroResumo && <p className="erro-operacao">Não foi possível carregar o resumo.</p>}
                {resumo && resumo.length > 0 && (
                    <ul className="resumo-do-grupo" aria-label="Resumo por fase">
                        {resumo.map((fase) => (
                            <li key={fase.fase}>
                                <span className="rotulo-resumo">{rotuloDoResumo(fase)}</span>
                                <span className="texto-resumo">{textoDoResumo(fase)}</span>
                                {fase.tempo_ms != null && (
                                    <span className="tempo-resumo" data-testid="tempo-do-lider-resumo">
                                        Tempo do líder: {formatarTempoDoLider(fase.tempo_ms)}
                                    </span>
                                )}
                                {/* O servidor diz o botão (`comando`); só com fase aberta nenhuma caixa oferece (o grupo tem uma
                                    vaga só). Grupo com tudo feito ("Finalizado") continua podendo reiniciar. */}
                                {!cardAtual.fase_atual && fase.comando && (
                                    <button
                                        type="button"
                                        className={`botao-iniciar-fase botao-${fase.comando}-fase`}
                                        aria-label={`${VERBO_DO_COMANDO[fase.comando]} ${fase.rotulo}`}
                                        onClick={() => escolherIniciar(fase.fase, fase.rotulo, fase.comando ?? 'iniciar')}
                                        disabled={processando}
                                    >
                                        {VERBO_DO_COMANDO[fase.comando]}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                <section className="participantes-do-grupo" aria-labelledby={`${idDoTitulo}-participantes`}>
                    <h3 id={`${idDoTitulo}-participantes`}>Participantes</h3>
                    <label className="pesquisa-modal-grupo">
                        Pesquisar
                        <input
                            type="text"
                            value={pesquisa}
                            placeholder="Pesquise pelo nome ou matrícula"
                            onChange={(evento) => setPesquisa(evento.target.value)}
                        />
                    </label>
                    {membros && (
                        <form
                            className="alocar-no-grupo"
                            onSubmit={(evento) => {
                                evento.preventDefault();
                                if (buscarParaAlocar(matriculaParaAlocar)) setMatriculaParaAlocar('');
                            }}
                        >
                            <label>
                                Alocar
                                <input
                                    type="text"
                                    value={matriculaParaAlocar}
                                    placeholder="Digite a matrícula"
                                    onChange={(evento) => setMatriculaParaAlocar(evento.target.value)}
                                />
                            </label>
                            <button type="submit" className="botao-alocar">
                                <span aria-hidden="true">+</span> Alocar
                            </button>
                        </form>
                    )}
                    {erroMembros && <p className="erro-operacao">Não foi possível carregar os participantes.</p>}
                    {membros && (
                        <>
                            <table className="tabela-modal-grupo" aria-label="Participantes do grupo">
                                <thead>
                                    <tr>
                                        <th scope="col">Matrícula</th>
                                        <th scope="col">Nome</th>
                                        <th scope="col">Papel</th>
                                        <th scope="col">Situação</th>
                                        <th scope="col">Conexão</th>
                                        <th scope="col">Tablet</th>
                                        <th scope="col">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visiveis.map((membro) => (
                                        <tr key={membro.id}>
                                            <td className="matricula">{membro.login}</td>
                                            <td>{membro.nome ?? '–'}</td>
                                            <td>{membro.lider ? <span className="selo-lider">Líder</span> : 'Participante'}</td>
                                            <td>{SITUACAO[membro.situacao]}</td>
                                            <td>
                                                <SeloDeConexao conexao={membro.conexao} />
                                            </td>
                                            <td>{membro.tablet ?? '–'}</td>
                                            <td className="coluna-acao">
                                                <MenuSuspenso
                                                    rotulo={`Ações de ${membro.login}`}
                                                    itens={itensDaLinha(membro, membros)}
                                                    aberto={menuAberto === membro.id}
                                                    aoAlternar={() => alternarMenu(membro.id)}
                                                    aoFechar={fecharMenu}
                                                    aoEscolher={(acao) => escolher(membro, acao)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {visiveis.length === 0 && <p className="vazio-operacao">Nenhum participante encontrado.</p>}
                        </>
                    )}
                </section>
                {/* `DP-RA-045`: o gatilho do "Excluir Grupo NN?" fica no rodapé do modal. */}
                <div className="rodape-modal-grupo">
                    <button type="button" className="botao-excluir-grupo" onClick={escolherExcluirGrupo} disabled={processando}>
                        Excluir grupo
                    </button>
                </div>
            </div>
            {aviso && <Aviso aviso={aviso} aoFechar={fecharAviso} />}
            {confirmacao?.tipo === 'situacao' && (
                <ConfirmacaoDeSituacao
                    participante={{ nome: confirmacao.jogador.nome ?? null, matricula: confirmacao.jogador.login }}
                    acao={confirmacao.acao}
                    fraseDoLider={
                        confirmacao.acao === 'inativar' ? fraseDoSorteio(confirmacao.jogador, rotuloDoGrupo) : undefined
                    }
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void enviar(confirmacao.jogador, confirmacao.acao)}
                />
            )}
            {confirmacao?.tipo === 'lider' && (
                <ConfirmacaoDeLideranca
                    nome={confirmacao.jogador.nome ?? confirmacao.jogador.login}
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void enviar(confirmacao.jogador, 'lider')}
                />
            )}
            {confirmacao?.tipo === 'desfazer' && (
                <ConfirmacaoDeDesfazer
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void enviar(confirmacao.jogador, 'desfazer')}
                />
            )}
            {confirmacao?.tipo === 'pontuacao' && (
                <PontuacaoDoJogador
                    turmaId={turmaId}
                    jogadorId={confirmacao.jogador.id}
                    nome={confirmacao.jogador.nome ?? confirmacao.jogador.login}
                    aoFechar={fecharConfirmacao}
                />
            )}
            {confirmacao?.tipo === 'excluir' && (
                <ConfirmacaoDeExcluirParticipante
                    nome={confirmacao.jogador.nome ?? confirmacao.jogador.login}
                    fraseDoLider={fraseDoSorteio(confirmacao.jogador, rotuloDoGrupo)}
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void excluir(confirmacao.jogador)}
                />
            )}
            {confirmacao?.tipo === 'excluirGrupo' && (
                <ConfirmacaoDeExcluirGrupo
                    rotulo={rotuloDoGrupo}
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void excluirOGrupo()}
                />
            )}
            {confirmacao?.tipo === 'estacao' && (
                <ConfirmacaoDeEstacao
                    acao={confirmacao.acao}
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void enviarEstacao(confirmacao.acao, confirmacao.fase)}
                />
            )}
            {confirmacao?.tipo === 'iniciar' && (
                <ConfirmacaoDeInicio
                    acao={confirmacao.acao}
                    rotulo={confirmacao.rotulo}
                    grupo={rotuloDoGrupo}
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void enviarIniciar(confirmacao.fase, confirmacao.rotulo, confirmacao.acao)}
                />
            )}
            {confirmacao?.tipo === 'alocar' && (
                <ConfirmacaoDeAlocacao
                    nome={confirmacao.jogador.nome ?? confirmacao.jogador.login}
                    origem={confirmacao.jogador.grupo_id ? `Grupo ${confirmacao.jogador.grupo_id}` : null}
                    destino={rotuloDoGrupo}
                    fraseDoLider={
                        confirmacao.jogador.grupo_id
                            ? fraseDoSorteio(confirmacao.jogador, `Grupo ${confirmacao.jogador.grupo_id}`)
                            : undefined
                    }
                    processando={processando}
                    aoCancelar={fecharConfirmacao}
                    aoConfirmar={() => void enviar(confirmacao.jogador, 'alocar')}
                />
            )}
        </div>
    );
}
